import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { TRANSLATIONS } from "./translations";
import { ACTIVE_CODES, REGISTERED_CODES, fallbackChain, isActiveLang, dirOf } from "./languages";

const DEFAULT_LANG = "en";

// Phase A18-B1 — preferred-language persistence after login.
// `tsw_lang_source` distinguishes a language choice made *manually* by the
// user (via the LanguageDropdown) from one applied *automatically* from
// the user's saved profile preference. AuthContext.login/refresh apply
// the saved profile language ONLY when `tsw_lang_source !== "manual"`,
// so a manual mid-session switch is never overridden. `logout()` clears
// the flag so the next user on the same browser starts fresh.
const LANG_SOURCE_KEY = "tsw_lang_source";
const SOURCE_MANUAL = "manual";
const SOURCE_AUTO = "auto";
const _readLangSource = () => {
  try { return typeof localStorage !== "undefined" ? localStorage.getItem(LANG_SOURCE_KEY) : null; } catch { return null; }
};
const _writeLangSource = (val) => {
  try {
    if (typeof localStorage === "undefined") return;
    if (val == null) localStorage.removeItem(LANG_SOURCE_KEY);
    else localStorage.setItem(LANG_SOURCE_KEY, val);
  } catch { /* ignore quota / disabled-storage */ }
};

// Dev-only missing-translation telemetry. We log each missing (lang, key)
// combo exactly once per session so Phase-4 onboarding teams can audit
// coverage without spamming the console. Production builds (NODE_ENV=
// "production") stay silent. The Set is module-scoped, not per-render,
// so React StrictMode double-mounts don't flood the log either.
const __MISSING_I18N__ = new Set();
const __logMissing = (lang, key) => {
  if (typeof process !== "undefined" && process.env && process.env.NODE_ENV === "production") return;
  const stamp = `${lang}::${key}`;
  if (__MISSING_I18N__.has(stamp)) return;
  __MISSING_I18N__.add(stamp);
  // eslint-disable-next-line no-console
  console.warn(`[i18n] missing translation: lang="${lang}" key="${key}" (falling back to EN)`);
};

const LangContext = createContext({ lang: DEFAULT_LANG, t: () => "", setLang: () => {} });

// =============================================================================
// Phase A18-B3 — module-level translation bridge for non-React call sites.
//
// `lib/api.js` (the axios error formatter) lives outside the React tree and
// therefore cannot call `useLang()`. To localize its fallback strings without
// re-architecting every call site, we expose a tiny global mirror that the
// LangProvider keeps in sync with the active language.
//
//   getCurrentLang() -> active code (defaults to "en" before the provider mounts)
//   tGlobal(path, vars?) -> same resolution algorithm as the React `t()`
//
// This is a *read-only* mirror: there is no setter; the provider writes to
// `_currentLang` whenever its state changes. The fallback algorithm walks
// the same `fallbackChain` and `TRANSLATIONS` table as the in-tree `t()`,
// so behavior is identical from a consumer's perspective.
// =============================================================================
let _currentLang = DEFAULT_LANG;
const _resolveInDict = (dict, parts) => {
  let node = dict;
  for (const p of parts) {
    if (node && typeof node === "object" && p in node) node = node[p];
    else return undefined;
  }
  return typeof node === "string" ? node : undefined;
};
export const getCurrentLang = () => _currentLang;
export const tGlobal = (path, vars) => {
  if (!path || typeof path !== "string") return "";
  const parts = path.split(".");
  let str;
  for (const code of fallbackChain(_currentLang)) {
    const dict = TRANSLATIONS[code];
    if (!dict) continue;
    str = _resolveInDict(dict, parts);
    if (str !== undefined) break;
  }
  if (str === undefined) str = path;
  if (vars) {
    return Object.keys(vars).reduce(
      (acc, k) => acc.replaceAll(`{${k}}`, vars[k]),
      str,
    );
  }
  return str;
};


const detectInitialLang = () => {
  const saved = typeof localStorage !== "undefined" ? localStorage.getItem("tsw_lang") : null;
  if (saved && isActiveLang(saved)) return saved;
  if (typeof navigator !== "undefined" && navigator.language) {
    const nav = navigator.language.toLowerCase();
    // Match the active language codes by the most specific prefix first.
    const match = ACTIVE_CODES.find((c) => nav === c || nav.startsWith(c + "-") || nav.startsWith(c));
    if (match) return match;
  }
  return DEFAULT_LANG;
};

export const LangProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectInitialLang);

  useEffect(() => {
    if (typeof localStorage !== "undefined") localStorage.setItem("tsw_lang", lang);
    // Phase A18-B3 — mirror the active language to the module-level
    // bridge so `lib/api.js` (and any other non-React consumer) can call
    // `tGlobal()` without needing access to the React context.
    _currentLang = lang;
    if (typeof document !== "undefined") {
      // Phase A4: keep `<html lang>` AND `<html dir>` in sync with the
      // active language. `dirOf` returns "ltr" for every approved
      // language today, so this is a no-op until AR or HE is activated.
      // Layout safety: only the html attribute changes — no global CSS
      // class is toggled, so RTL has no effect on LTR users.
      document.documentElement.lang = lang;
      document.documentElement.dir = dirOf(lang);
    }
  }, [lang]);

  // Only let consumers switch to languages that are CURRENTLY active.
  // A `force` option exists so that future tooling (admin previews,
  // marketing site, automated translation QA) can preview a registered-but-
  // not-active language without flipping the global active flag.
  const setLang = useCallback((code, opts = {}) => {
    if (opts.force && REGISTERED_CODES.includes(code)) {
      setLangState(code);
      return;
    }
    setLangState(isActiveLang(code) ? code : DEFAULT_LANG);
  }, []);

  // Phase A18-B1 — explicit MANUAL switch (LanguageDropdown). Marks the
  // browser as having an active user choice so AuthContext.login/refresh
  // will not override it later in the session.
  const setLangManual = useCallback((code) => {
    _writeLangSource(SOURCE_MANUAL);
    if (REGISTERED_CODES.includes(code)) {
      setLangState(isActiveLang(code) ? code : DEFAULT_LANG);
    }
  }, []);

  // Phase A18-B1 — AUTOMATIC apply from `user.language` (called by
  // AuthContext after login/register/refresh). Respects an existing
  // manual choice: if the user already picked something explicitly in
  // this session, never overwrite it silently. The very first apply
  // sets the source to "auto" so subsequent auto-applies remain a no-op
  // when the value is already correct.
  const setLangAuto = useCallback((code) => {
    if (!code || !isActiveLang(code)) return;
    if (_readLangSource() === SOURCE_MANUAL) return;
    _writeLangSource(SOURCE_AUTO);
    setLangState((prev) => (prev === code ? prev : code));
  }, []);

  // Phase A18-B1 — clear the source marker on logout so the next user
  // signing in on the same browser starts from a clean slate (their
  // saved preference will auto-apply via setLangAuto).
  const clearLangSource = useCallback(() => {
    _writeLangSource(null);
  }, []);


  // Pick a translated value from a multilingual document by walking the
  // language's fallback chain, looking for `${base}_${code}` fields.
  // Field names use underscores (so `pt-br` becomes `pt_br`) to stay
  // compatible with MongoDB's typical key naming.
  const pickI18n = useCallback((doc, base) => {
    if (!doc) return "";
    for (const code of fallbackChain(lang)) {
      const k = `${base}_${code.replace(/-/g, "_")}`;
      if (doc[k]) return doc[k];
    }
    if (doc[base]) return doc[base];
    return "";
  }, [lang]);

  // Resolve a UI string from the in-memory dictionary. If the active
  // language has no entry, walk the fallback chain through TRANSLATIONS,
  // and finally return the literal path so missing keys are visible
  // during development.
  const t = useCallback((path, vars) => {
    const parts = path.split(".");
    const resolveIn = (dict) => {
      let node = dict;
      for (const p of parts) {
        if (node && typeof node === "object" && p in node) node = node[p];
        else return undefined;
      }
      return typeof node === "string" ? node : undefined;
    };
    let str;
    let resolvedAt = null;
    for (const code of fallbackChain(lang)) {
      const dict = TRANSLATIONS[code];
      if (!dict) continue;
      str = resolveIn(dict);
      if (str !== undefined) { resolvedAt = code; break; }
    }
    if (str === undefined) {
      __logMissing(lang, path);
      str = path;
    } else if (resolvedAt && resolvedAt !== lang) {
      // Translation resolved via the fallback chain, not the active
      // language — flag it so we can prioritize the missing entry.
      __logMissing(lang, path);
    }
    if (vars) {
      return Object.keys(vars).reduce((acc, k) => acc.replaceAll(`{${k}}`, vars[k]), str);
    }
    return str;
  }, [lang]);

  // P5.4 — Resolve a NON-STRING leaf (object/array/number/bool) by the
  // same dot-path. Used by Lab Welcome Wizard to pull a per-lab content
  // bundle (`labWizard.labs.racket_lab`) and its nested bullet arrays.
  // Walks the language fallback chain just like t(). Returns `undefined`
  // (not the literal path string) when the path is missing so callers
  // can apply their own typed fallback.
  const tNode = useCallback((path) => {
    const parts = path.split(".");
    const resolveIn = (dict) => {
      let node = dict;
      for (const p of parts) {
        if (node && typeof node === "object" && p in node) node = node[p];
        else return undefined;
      }
      return node;
    };
    for (const code of fallbackChain(lang)) {
      const dict = TRANSLATIONS[code];
      if (!dict) continue;
      const node = resolveIn(dict);
      if (node !== undefined) return node;
    }
    return undefined;
  }, [lang]);

  // ── Phase A3.1-fix-4 centralized display helpers ─────────────────────
  // tProfile(key, fallbackDoc?) — resolves a profile_key to its localized
  // label using the `profiles.<key>` namespace, falling back to backend
  // `pickI18n(doc, "label")` and finally to the raw key. Keeps the
  // internal value (key) intact for `<option value>` etc.
  const tProfile = useCallback((key, fallbackDoc) => {
    if (!key) return "—";
    const r = t(`profiles.${key}`);
    if (r && r !== `profiles.${key}`) return r;
    if (fallbackDoc) {
      const fb = pickI18n(fallbackDoc, "label");
      if (fb) return fb;
      if (fallbackDoc.label_en) return fallbackDoc.label_en;
    }
    return key;
  }, [t, pickI18n]);

  // tCountry(isoCode, fallbackDoc?) — renders a 2-letter ISO country code
  // in the active UI language using Intl.DisplayNames (preserves the
  // stored value unchanged). Falls back to the backend doc's pickI18n
  // name_en/_es/_zh, then to the raw code.
  const _displayNamesByLang = useMemo(() => ({ ref: {} }), []);
  const tCountry = useCallback((code, fallbackDoc) => {
    if (!code) return "—";
    const norm = String(code).toUpperCase();
    try {
      // Intl uses BCP-47 tags. Our active langs map 1:1 except "pt-br".
      const tag = lang === "pt-br" ? "pt-BR" : lang;
      let dn = _displayNamesByLang.ref[tag];
      if (!dn && typeof Intl !== "undefined" && Intl.DisplayNames) {
        dn = new Intl.DisplayNames([tag], { type: "region", fallback: "code" });
        _displayNamesByLang.ref[tag] = dn;
      }
      const named = dn && dn.of(norm);
      if (named && named !== norm) return named;
    } catch { /* Intl unavailable / bad code — fall through */ }
    if (fallbackDoc) {
      const fb = pickI18n(fallbackDoc, "name");
      if (fb) return fb;
      if (fallbackDoc.name_en) return fallbackDoc.name_en;
    }
    return norm;
  }, [lang, pickI18n, _displayNamesByLang]);

  // tQuestionnaireTitle(q) — preserves backend localized title when it
  // exists in the active language; otherwise tries a UI-side override
  // dictionary keyed by a slug derived from title_en (so admin-edited
  // titles still resolve); finally returns the English title or an
  // "archived/unnamed" sentinel. Database IDs/schemas untouched.
  const _slugifyTitle = (s) => String(s || "")
    .toLowerCase()
    .replace(/[·•—–]/g, " ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const tQuestionnaireTitle = useCallback((q) => {
    if (!q) return "";
    // 1. Backend-localized title for the current lang only (don't accept
    //    title_en as a "match" — we want to try the UI override below).
    const code = String(lang).replace("-", "_");
    if (q[`title_${code}`]) return q[`title_${code}`];
    // 2. UI override keyed by slug derived from title_en.
    const slug = _slugifyTitle(q.title_en || q.title || "");
    if (slug) {
      const r = t(`questionnaireTitles.${slug}`);
      if (r && r !== `questionnaireTitles.${slug}`) return r;
    }
    // 3. Backend fallback chain (es/zh/etc).
    const fb = pickI18n(q, "title");
    if (fb) return fb;
    // 4. English raw title.
    if (q.title_en) return q.title_en;
    return q.is_orphan
      ? t("common.archivedQuestionnaire")
      : t("common.unnamedQuestionnaire");
  }, [lang, t, pickI18n]);

  // Memoize so consumers of useLang() don't re-render on every parent re-render.
  // `dir` exposes the active language's writing direction so consumers can
  // conditionally adjust visuals without re-importing the registry. Today
  // it is always "ltr" because the active language set is LTR-only — the
  // Phase A4 RTL languages (ar / he) remain inactive.
  const dir = dirOf(lang);
  const value = useMemo(
    () => ({ lang, dir, setLang, setLangManual, setLangAuto, clearLangSource, t, tNode, pickI18n, tProfile, tCountry, tQuestionnaireTitle }),
    [lang, dir, setLang, setLangManual, setLangAuto, clearLangSource, t, tNode, pickI18n, tProfile, tCountry, tQuestionnaireTitle]
  );

  return (
    <LangContext.Provider value={value}>{children}</LangContext.Provider>
  );
};

export const useLang = () => useContext(LangContext);
